import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import GSheetDialogAlert from '@consumer/components/GSheetDialogAlert.vue'
import GSheetDialogConfirm from '@consumer/components/GSheetDialogConfirm.vue'
import { camelCase, uniqueId } from '@consumer/helpers/string'
import FeeSchedule from '@consumer/modules/checkout/FeeSchedule.vue'
import type { GiftWizardProps } from '@consumer/modules/checkout/GiftWizardModal.vue'
import type { CustomizerModalProps } from '@consumer/modules/customizer/CustomizerModal.vue'
import type { DemoStep } from '@consumer/modules/shared/HowGiftlyWorksModal.vue'
import { addModal, removeModals, type BaseModal, type ModalComponent } from '@consumer/stores/modals'
import type GiftTemplate from '@consumer/types/GiftTemplate'
import type { CalendarEvent, CalendarEventInstance, MultiplaceGift, RecipientGift } from '@consumer/types/serializers'
import { api } from '@corp/services/progress'
import { notifyError } from '@shared/errors'
import { isPromise } from '@shared/promises'
import { mergeProps, type AsyncComponentLoader } from 'vue'
import type { IconDefinition } from '@fortawesome/pro-light-svg-icons'

export const CUSTOMIZER_MODAL_ID = 'customizer-modal'
export const GIFT_WIZARD_MODAL_ID = 'gift-wizard'

interface ModalArgs {
  name?: string
  header?: any
  content: any
  props?: any
}

interface AlertModalArgs extends ModalArgs {
  danger?: boolean
  onClose?: Function
}

interface ConfirmModalArgs<Y, N = void> extends ModalArgs {
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: Promise<Y> | (() => Y)
  onCancel?: () => N
  onClose?: () => N
  danger?: boolean
}

type ComponentPromise<T> = ReturnType<AsyncComponentLoader<T>>

// Public: Adds a new modal to the ModalManager with the specified configuration.
//
// Returns the opened modal id.
export function openModal<P> (
  component: ModalComponent<P>,
  modal: Omit<BaseModal, 'component'>,
  reStack = false,
) {
  return addModal({ component, ...modal }, reStack)
}

// Shared scroll position for dialogs to preserve when they open
// eslint-disable-next-line import/no-mutable-exports
export let savedScrollPositionForDialog = 0

// Public: Opens a modal component that wasn't pre-bundled.
//
// NOTE: By default the modal component path is used to create a static id,
// which prevents duplicates.
//
// NOTE: Pass `id: undefined` in the options to allow several instances of the
// same modal to be opened at the same time.
//
// Returns a promise that resolves to the opened modal id.
export function asyncOpenModal<P> (
  componentPromise: ComponentPromise<ModalComponent<P>>,
  { id, ...attrs }: BaseModal<P>['attrs'] & { id?: string } = {},
  reStack = false,
) {
  // Save scroll position before any async operations
  savedScrollPositionForDialog = window.scrollY

  api?.startNavigation()
  return componentPromise
    .then((component) => {
      openModal((component as any).default || component, { attrs, id }, reStack)
    })
    .catch((error: any) => {
      notifyError(error)
      openErrorAlertModal({ content: 'An unexpected error occurred, please try reloading the page' })
    })
    .finally(() => api?.finishNavigation())
}

export const openAlertModal = ({ name, header, content, danger = false, props = {}, onClose }: AlertModalArgs) => {
  // Cleanup previous alert modal if it exists
  removeModals('alert')

  addModal({
    component: GSheetDialogAlert,
    type: 'alert',
    attrs: mergeProps({
      name: name ?? uniqueId('modal-service-alert-'),
      danger,
      open: true,
    }, props ?? {}),
    eventListeners: {
      close: onClose,
    },
    slots: {
      header: header ? () => header : undefined,
      default: () => content,
    },
  })
}

// Public: Opens a confirmation dialog with the specified options.
//
// Returns a promise that resolves to the opened dialog id.
export function modalConfirm<Y, N = void> (
  attrs: ConfirmModalArgs<Y, N>,
) {
  if (attrs.name === undefined)
    attrs.name = `Confirm ${attrs.confirmLabel}`

  const promise: Promise<Y | N | undefined> = new Promise((resolve, reject) => {
    const onCancel = () => {
      resolve(attrs.onCancel?.())
      return promise
    }

    const onClose = () => {
      resolve(attrs.onClose?.())
      return promise
    }

    const onConfirm = async () => {
      try {
        resolve(await (isPromise(attrs.onConfirm) ? attrs.onConfirm : attrs.onConfirm?.()))
      }
      catch (error) {
        reject(error)
      }
      return promise
    }
    return openModal(GSheetDialogConfirm, {
      attrs: { width: 'mediumwide', open: true, ...attrs, onConfirm, onCancel, onClose },
      id: camelCase(attrs.header || 'confirmModal'),
      slots: {
        header: attrs.header ? () => attrs.header : undefined,
        default: () => attrs.content,
      },
    })
  })
  return promise
}

export { modalConfirm as openConfirmModal }

export function openDeleteModal<Y, N = void> ({
  confirmLabel = 'Delete',
  header = 'Are you sure?',
  ...options
}: ConfirmModalArgs<Y, N>) {
  return modalConfirm<Y, N>({ confirmLabel, danger: true, header, ...options })
}

export const openErrorAlertModal = ({
  name,
  header = 'Oops, something went wrong',
  content,
  onClose,
}: {
  name?: string
  header?: string
  content?: any
  onClose?: Function
}) => {
  openAlertModal({
    name: name ?? uniqueId('modal-service-error-alert-'),
    danger: true,
    header,
    content,
    onClose,
  })
}

interface HowGiftlyWorksModalProps {
  initialStep?: DemoStep
  gift?: GiftTemplate
}

export function openHowGiftlyWorksModal (props: HowGiftlyWorksModalProps = {}) {
  return asyncOpenModal(import('@consumer/modules/shared/HowGiftlyWorksModal.vue'), {
    id: 'how-giftly-works-modal',
    ...props,
  })
}

interface GiftShowcaseModalProps {
  // demo   — generic sample gift, fetched from the backend when no gift given.
  // preview — the buyer's actual (in-progress) gift.
  mode: 'demo' | 'preview'
  gift?: RecipientGift | GiftTemplate
}

export function openGiftShowcaseModal (props: GiftShowcaseModalProps) {
  return asyncOpenModal(
    import('@consumer/modules/recipientGiftLandingV2/staticCardsV2/GiftShowcaseModal.vue'),
    { id: 'gift-showcase-modal', ...props },
  )
}

export function openCustomizerModal (props: CustomizerModalProps, reStack = false) {
  return asyncOpenModal(import('@consumer/modules/customizer/CustomizerModal.vue'), {
    id: CUSTOMIZER_MODAL_ID,
    ...props,
  }, reStack)
}

export async function saveMultiplace (props: CustomizerModalProps, updatedMultiplaceGift: MultiplaceGift) {
  const placeIds = updatedMultiplaceGift.places!.map(place => place.giftlyId)
  const { newPlaces } = await MultiPlaceGiftIdeasApi.updatePlaces({
    params: updatedMultiplaceGift,
    data: { placeIds },
  })

  props.gift.places = newPlaces
  if (props.multiplaceGift) {
    props.multiplaceGift.places = newPlaces
  }

  if (updatedMultiplaceGift.giftTags) {
    const tags = await MultiPlaceGiftIdeasApi.updateGiftTags({
      params: updatedMultiplaceGift,
      data: { giftTags: updatedMultiplaceGift.giftTags },
    })

    if (props.multiplaceGift) {
      props.multiplaceGift.giftTags = tags
    }
  }

  if (props.multiplaceGift && props.gift) {
    props.multiplaceGift.amounts = updatedMultiplaceGift.amounts
    props.gift.giftlyMetadata = toRaw(updatedMultiplaceGift.giftlyMetadata)
    props.multiplaceGift.giftlyMetadata = toRaw(updatedMultiplaceGift.giftlyMetadata)
    props.gift.title = updatedMultiplaceGift.title
    props.multiplaceGift.title = updatedMultiplaceGift.title
    props.multiplaceGift.giftSeries = updatedMultiplaceGift.giftSeries

    const { amounts, giftlyMetadata, title, giftSeries } = updatedMultiplaceGift
    const updatedGift = await MultiPlaceGiftIdeasApi.update({
      params: updatedMultiplaceGift,
      data: { amounts, giftlyMetadata, title, giftSeriesId: giftSeries?.id },
    })

    if (updatedGift) {
      Object.assign(props.multiplaceGift, updatedGift)
      if (updatedGift.giftlyMetadata) {
        props.gift.giftlyMetadata = toRaw(updatedGift.giftlyMetadata)
      }
    }
  }
}

export function openMultiplaceCustomizerModal (props: CustomizerModalProps) {
  return asyncOpenModal(import('@consumer/modules/customizer/CustomizerModal.vue'), {
    id: CUSTOMIZER_MODAL_ID,
    async onSaveMultiplace (updatedMultiplaceGift: MultiplaceGift) {
      saveMultiplace(props, updatedMultiplaceGift)
    },
    ...props,
  })
}

export function openGiftWizard (props: GiftWizardProps) {
  return asyncOpenModal(import('@consumer/modules/checkout/GiftWizardModal.vue'), {
    id: GIFT_WIZARD_MODAL_ID,
    ...props,
  })
}

export function openFeeScheduleModal () {
  openAlertModal({
    name: 'fee-schedule-modal',
    header: h('div', { class: 'font-semibold text-xl' }, 'Why is there a purchase fee'),
    content: h(FeeSchedule),
    props: { class: '' },
  })
}

export function openAddEventDialog (onSuccess: (event: CalendarEvent) => void, initialSubtype?: string) {
  return asyncOpenModal(import('@consumer/modules/myAccount/calendar/AddEventDialog.vue'), {
    id: 'add-calendar-event-dialog',
    onSuccess,
    initialSubtype,
  })
}

export function openViewEventDialog (
  eventInstance: CalendarEventInstance,
  { openInvitations = false, autoAdd = false, justAdded = false } = {},
) {
  return asyncOpenModal(import('@consumer/modules/myAccount/calendar/ViewEventDialog.vue'), {
    id: 'view-event-dialog',
    eventInstance,
    open: true,
    openInvitations,
    autoAdd,
    justAdded,
  })
}

export function openEditEventInvitationsDialog (eventInstance: CalendarEventInstance) {
  return asyncOpenModal(import('@consumer/modules/myAccount/calendar/EditEventInvitationsDialog.vue'), {
    id: 'edit-event-invitations-dialog',
    eventInstance,
  })
}

interface LoginOrSignupModalProps {
  email?: string
  name?: string
  title?: string
  subtitle?: string
  icon?: IconDefinition | string
  emailReadonly?: boolean
  calendarEventInvitationToken?: string
  initialScreen?: 'email' | 'password' | 'signup' | 'confirmSignup'
  redirectTo?: string
  hideBackgroundLogo?: boolean
  submitLabel?: string
  signupConfirmation?: boolean
}

export function openAddUsernameModal (suggestedUsername: string) {
  return asyncOpenModal(import('@consumer/modals/AddUsernameModal.vue'), {
    id: 'add-username-modal',
    suggestedUsername,
  })
}

export function openLoginOrSignupModal (props: LoginOrSignupModalProps = {}) {
  return asyncOpenModal(import('@consumer/modals/loginOrSignup/LoginOrSignUpModal.vue'), {
    id: 'login-or-signup-modal',
    ...props,
  })
}
